import React from "react";
import { IconExternalLink, IconFile } from "@tabler/icons-react";
import clsx from "clsx";
import {
  LoadingState,
  OverviewProps,
} from "../features/EPDSearch2050/SearchTable";
import { EPDSearchOUT, Material2050SearchOUT } from "api-client";
import { epdSearchStore } from "features/EPDSearchModal/EPDSearchStore";
import EPDRow from "./EPDRow";
import ExternalEPDRow from "./ExternalEPDRow";
import Button from "components/Button";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ROUTES_ENUM } from "routes/consts";
import HoverMenu from "./HoverMenu";

interface EPDRowProps {
  epd: Material2050SearchOUT | EPDSearchOUT;
  showOverview: OverviewProps;
  setOpenOverview: (item: Material2050SearchOUT | EPDSearchOUT) => void;
  handleSelectEPD: (epd: Material2050SearchOUT | EPDSearchOUT) => void;
  loadingEPDSelect: LoadingState;
  generate?: boolean;
}

const SearchRow = ({ epd, ...props }: EPDRowProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openEPDUrl = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(String((epd as EPDSearchOUT).url), "_blank");
  };

  const onSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    props.handleSelectEPD(epd);
  };

  const onGenerateClicked = () => {
    navigate(`${ROUTES_ENUM.PRODUCT_EDITOR}`, {
      state: {
        id: epd.id,
        mode: "generate",
        isExternal: epdSearchStore.isExternalSearch,
      },
    });
  };

  const showButton = () => {
    if (props.generate)
      return (
        <Button onClick={onGenerateClicked}>
          {t("productEditor.generateProduct")}
        </Button>
      );

    if (epdSearchStore.isOrganisationalSearch)
      return <HoverMenu epd={epd as EPDSearchOUT} />;

    return (
      <Button
        loading={
          props.loadingEPDSelect.id === epd.id && props.loadingEPDSelect.state
        }
        onClick={onSelect}
        disable={props.loadingEPDSelect.state}
      >
        {t("commons.select")}
      </Button>
    );
  };

  function hoverRow() {
    return (
      <div
        className={clsx(
          "absolute hidden items-center justify-center right-[1px] top-[1px]",
          "h-[97%] group-hover/listItem:flex"
        )}
      >
        <div className="w-10 bg-gradient-to-r from-transparent to-indigo-50 h-full"></div>
        <div className="bg-indigo-50 flex h-full pr-3 items-center">
          {(epd as EPDSearchOUT)?.url &&
            !epdSearchStore.isOrganisationalSearch && (
              <IconExternalLink
                className="text-indigo-700 cursor-pointer mr-3"
                onClick={openEPDUrl}
                stroke={2.5}
              />
            )}
          {showButton()}
        </div>
      </div>
    );
  }

  return (
    <li
      className={clsx(
        "flex relative border-b border-gray-300 hover:bg-indigo-50 group/listItem overflow-hidden",
        {
          "h-16": !props.showOverview.state && epdSearchStore.isExternalSearch,
          "bg-indigo-50 ": props.showOverview.epd?.id === epd.id,
          "shadow-[inset_0_0_0_1px] shadow-indigo-600":
            props.showOverview.epd?.id === epd.id,
        }
      )}
      onClick={() => props.setOpenOverview(epd)}
    >
      <div className="flex px-3 py-2 w-full">
        <IconFile
          stroke={3}
          className="bg-pink-300 text-pink-900 p-1 rounded-full mr-[10px] min-w-[24px]"
        />
        {epdSearchStore.isExternalSearch ? (
          <ExternalEPDRow epd={epd as Material2050SearchOUT} />
        ) : (
          <EPDRow epd={epd as EPDSearchOUT} />
        )}
      </div>
      {(!epdSearchStore.isExternalSearch || props.generate) && hoverRow()}
    </li>
  );
};

export default SearchRow;
